/**
 * Kit Builder — Serene Le Parfum
 * --------------------------------------------------
 * Construido para colección "Crea tu Kit Serené 30 ml" — 8 fragancias 30 ml + Bag Serené.
 *
 * Componentes expuestos:
 *   window.KitBuilder          → state manager global (localStorage + events)
 *   <kit-add-button>           → botón "Sumar al kit" en la card
 *   <kit-quick-add-modal>      → modal slim que reemplaza el quick-add nativo
 *   <kit-progress-bar>         → barra sticky con contador + precio combo
 *   <kit-sticky-cta>           → CTA fijo inferior en mobile
 *
 * El AJAX usa /cart/add.js y luego /cart.js para refrescar el estado real.
 * Nada pisa el tema: todo está namespaced .kit-* y window.KitBuilder.
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------
  // 1. STATE MANAGER (singleton)
  // ---------------------------------------------------------------
  const STORAGE_KEY = 'serene_kit_30ml_state';

  const KitBuilder = {
    config: {
      target: 8,                    // 8 fragancias para completar kit 30ml
      comboPrice: 27990000,         // 279.900 COP en centavos
      regularPrice: 48000000,       // 480.000 COP (8 × $60.000) en centavos
      unitSize: '30 ml',
      currency: 'COP',
      celebrationShown: false,
    },

    events: new EventTarget(),

    getState() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { items: [] };
        const parsed = JSON.parse(raw);
        return parsed && Array.isArray(parsed.items) ? parsed : { items: [] };
      } catch (e) {
        return { items: [] };
      }
    },

    setState(state) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch (e) {
        // quota exceeded / safari privado: silenciar
      }
      this.events.dispatchEvent(new CustomEvent('kit:change', { detail: state }));
    },

    addItem(item) {
      const state = this.getState();
      state.items.push({
        variantId: item.variantId,
        productId: item.productId,
        title: item.title,
        image: item.image,
        price: item.price,
        addedAt: Date.now(),
      });
      this.setState(state);

      if (state.items.length === this.config.target && !this.config.celebrationShown) {
        this.config.celebrationShown = true;
        this.events.dispatchEvent(new CustomEvent('kit:complete', { detail: state }));
      }
    },

    clear() {
      this.setState({ items: [] });
      this.config.celebrationShown = false;
    },

    hasProduct(productId) {
      return this.getState().items.some((i) => String(i.productId) === String(productId));
    },

    getCount() {
      return this.getState().items.length;
    },

    /**
     * Sincroniza el estado local contra /cart.js. Si el carrito real está vacío
     * (el cliente lo vació en otra pestaña o abre en una sesión nueva), limpia
     * el estado para que la barra no muestre "Kit completo" sin razón.
     */
    async syncWithCart() {
      try {
        const res = await fetch('/cart.js', {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!res.ok) return;
        const cart = await res.json();
        const cartVariantIds = (cart.items || []).map((i) => String(i.variant_id));
        const state = this.getState();

        // Cart vacío → reset total del kit
        if (cartVariantIds.length === 0 && state.items.length > 0) {
          this.clear();
          return;
        }

        // Filtrar items del kit que ya no están en el cart real
        const validItems = state.items.filter((i) => cartVariantIds.includes(String(i.variantId)));
        if (validItems.length !== state.items.length) {
          this.setState({ items: validItems });
        }
        // Reset del flag de celebración si ya no está completo
        if (validItems.length < this.config.target) {
          this.config.celebrationShown = false;
        }
      } catch (e) {
        // offline o endpoint caído: no romper UX
      }
    },

    formatPrice(cents) {
      try {
        return new Intl.NumberFormat('es-CO', {
          style: 'currency',
          currency: this.config.currency,
          maximumFractionDigits: 0,
        }).format(cents / 100);
      } catch (e) {
        return '$' + Math.round(cents / 100).toLocaleString('es-CO');
      }
    },
  };

  window.KitBuilder = KitBuilder;

  // ---------------------------------------------------------------
  // 1.b SILENT ADD HELPERS (2026-05-25)
  //
  //    Reemplazo del flujo popup → silent add directo desde la card.
  //    - updateCartSections: refresca cart-icon-bubble y cart-drawer
  //      preservando el className UI (no sobrescribe 'active').
  //    - silentAddProduct: fetch /products/{handle}.js para resolver
  //      variant default → POST /cart/add.js con sections → update +
  //      KitBuilder.addItem (que dispara kit:complete en la #8).
  //
  //    Inspirado en el patrón documentado en
  //    proyecto_upsell_agranda_kit_50mlb.md (preservar 'active').
  // ---------------------------------------------------------------
  function updateCartSections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach((id) => {
      const html = sections[id];
      if (!html) return;
      try {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        if (id === 'cart-icon-bubble') {
          const target = document.getElementById('cart-icon-bubble');
          const src =
            parsed.querySelector('#shopify-section-cart-icon-bubble') ||
            parsed.querySelector('.shopify-section');
          if (target && src) target.innerHTML = src.innerHTML;
        }
        if (id === 'cart-drawer') {
          const target = document.querySelector('cart-drawer');
          const src = parsed.querySelector('cart-drawer');
          if (target && src) {
            // CRÍTICO: solo sincronizar is-empty, NO sobrescribir className
            // (preserva 'active' = drawer abierto). Patrón validado en kit-50ml-b.
            target.classList.toggle('is-empty', src.classList.contains('is-empty'));
            target.innerHTML = src.innerHTML;
          }
        }
      } catch (e) { /* noop — nunca romper la card */ }
    });
  }

  async function silentAddProduct({ productUrl, productId, opener }) {
    if (!productUrl) return;
    const btn = opener && opener.tagName ? opener : null;
    const label = btn ? btn.querySelector('.kit-add-button__label') : null;
    const originalLabel = label ? label.textContent : '';

    if (btn) {
      if (btn.disabled || btn.classList.contains('is-loading')) return;
      btn.classList.add('is-loading');
      btn.disabled = true;
    }

    try {
      const handle = productUrl.split('/products/')[1].split('?')[0].split('/')[0];
      const productRes = await fetch('/products/' + handle + '.js', {
        headers: { Accept: 'application/json' },
      });
      if (!productRes.ok) throw new Error('Producto no disponible');
      const product = await productRes.json();
      const variant = product.variants.find((v) => v.available) || product.variants[0];
      if (!variant) throw new Error('Sin variantes');

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variant.id, quantity: 1 }],
          sections: 'cart-icon-bubble,cart-drawer',
        }),
      });
      if (!addRes.ok) {
        const err = await addRes.json().catch(() => ({}));
        throw new Error(err.description || 'No se pudo agregar');
      }
      const data = await addRes.json();

      // 1. Actualizar estado del kit (dispara kit:change y, en la #8, kit:complete)
      KitBuilder.addItem({
        variantId: variant.id,
        productId: product.id,
        title: product.title,
        image: (product.featured_image || '').replace(/_\d+x\d*(@2x)?\./, '_200x.'),
        price: variant.price,
      });

      // 2. Refrescar cart-icon-bubble y cart-drawer server-rendered (sin abrirlo)
      if (data.sections) updateCartSections(data.sections);

      // 3. Feedback visual mínimo en el botón
      if (label) label.textContent = '✓ Agregado';
      if (btn) {
        btn.classList.remove('is-loading');
        btn.classList.add('is-success');
      }
      setTimeout(() => {
        if (btn) {
          btn.classList.remove('is-success');
          btn.disabled = false;
        }
        if (label) {
          const inKit = KitBuilder.hasProduct(productId);
          label.textContent = inKit ? 'Agregar otra vez' : (originalLabel || 'Sumar al kit');
        }
      }, 1200);
    } catch (err) {
      if (label) label.textContent = 'Error, reintentá';
      if (btn) btn.classList.remove('is-loading');
      setTimeout(() => {
        if (btn) btn.disabled = false;
        if (label) label.textContent = originalLabel || 'Sumar al kit';
      }, 1800);
    }
  }

  // ---------------------------------------------------------------
  // 2. <kit-add-button>  → botón en la card (silent add 2026-05-25)
  // ---------------------------------------------------------------
  class KitAddButton extends HTMLElement {
    connectedCallback() {
      this.productId = this.dataset.productId;
      this.productUrl = this.dataset.productUrl;
      this.button = this.querySelector('button');
      if (!this.button) return;

      this.button.addEventListener('click', this.handleClick.bind(this));
      this.onStateChange = this.render.bind(this);
      KitBuilder.events.addEventListener('kit:change', this.onStateChange);
      this.render();
    }

    disconnectedCallback() {
      KitBuilder.events.removeEventListener('kit:change', this.onStateChange);
    }

    handleClick(e) {
      e.preventDefault();
      // Silent add — sin abrir popup intermedio.
      silentAddProduct({
        productUrl: this.productUrl,
        productId: this.productId,
        opener: this.button,
      });
    }

    render() {
      const inKit = KitBuilder.hasProduct(this.productId);
      this.classList.toggle('kit-add-button--in-kit', inKit);
      const label = this.button.querySelector('.kit-add-button__label');
      if (label && !this.button.classList.contains('is-loading') && !this.button.classList.contains('is-success')) {
        label.textContent = inKit ? 'Agregar otra vez' : 'Sumar al kit';
      }
    }
  }
  if (!customElements.get('kit-add-button')) {
    customElements.define('kit-add-button', KitAddButton);
  }

  // ---------------------------------------------------------------
  // 3. <kit-quick-add-modal>  → modal slim con fetch JSON
  // ---------------------------------------------------------------
  class KitQuickAddModal extends HTMLElement {
    constructor() {
      super();
      this.content = this.querySelector('.kit-modal__content');
      this.overlay = this.querySelector('.kit-modal__overlay');
      this.closeBtn = this.querySelector('.kit-modal__close');
      this.body = this.querySelector('.kit-modal__body');
      this.lastFocus = null;
      this.focusableSelector =
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

      this.addEventListener('keydown', this.handleKeydown.bind(this));
      if (this.overlay) this.overlay.addEventListener('click', this.hide.bind(this));
      if (this.closeBtn) this.closeBtn.addEventListener('click', this.hide.bind(this));

      // Delegación de eventos: un solo listener sobre el body del modal.
      // Sobrevive a innerHTML rehydrations, garantiza que el click del CTA
      // siempre se dispare aunque el markup interno se reescriba.
      if (this.body) {
        this.body.addEventListener('click', this.handleBodyClick.bind(this));
        this.body.addEventListener('change', this.handleBodyChange.bind(this));
        this.body.addEventListener('input', this.handleBodyInput.bind(this));
      }
    }

    handleBodyClick(e) {
      const submit = e.target.closest('[data-kit-submit]');
      if (submit) {
        e.preventDefault();
        this.handleSubmit({ currentTarget: submit });
        return;
      }
      const qtyBtn = e.target.closest('.kit-modal__qty-btn');
      if (qtyBtn) {
        e.preventDefault();
        const qtyInput = this.body.querySelector('.kit-modal__qty-input');
        if (!qtyInput) return;
        let v = parseInt(qtyInput.value, 10) || 1;
        v = qtyBtn.dataset.qty === 'plus' ? v + 1 : v - 1;
        v = Math.min(10, Math.max(1, v));
        qtyInput.value = v;
        this.quantity = v;
        return;
      }
      const errClose = e.target.closest('.kit-modal__error-close');
      if (errClose) {
        this.hide();
        return;
      }
    }

    handleBodyChange(e) {
      const variantRadio = e.target.closest('.kit-modal__variant-group input[type="radio"]');
      if (variantRadio) {
        this.handleVariantChange();
      }
      const qtyInput = e.target.closest('.kit-modal__qty-input');
      if (qtyInput) {
        let v = parseInt(qtyInput.value, 10) || 1;
        v = Math.min(10, Math.max(1, v));
        qtyInput.value = v;
        this.quantity = v;
      }
    }

    handleBodyInput(e) {
      const qtyInput = e.target.closest('.kit-modal__qty-input');
      if (qtyInput) {
        const v = parseInt(qtyInput.value, 10);
        if (!isNaN(v) && v >= 1 && v <= 10) this.quantity = v;
      }
    }

    connectedCallback() {
      // Mover el modal al <body> directamente para escapar cualquier ancestro
      // con transform/filter/clip-path que rompa el position: fixed.
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
      }
    }

    async show({ productUrl, productId, opener }) {
      this.lastFocus = opener || document.activeElement;
      this.body.innerHTML = this.renderSkeleton();
      this.setAttribute('open', '');
      document.documentElement.style.overflow = 'hidden';

      try {
        const handle = productUrl.split('/products/')[1].split('?')[0].split('/')[0];
        const res = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('Producto no disponible');
        const product = await res.json();
        this.product = product;
        this.selectedVariant = product.variants.find((v) => v.available) || product.variants[0];
        this.quantity = 1;
        this.renderProduct();
        this.trapFocus();
      } catch (e) {
        this.body.innerHTML = this.renderError();
      }
    }

    hide() {
      this.removeAttribute('open');
      document.documentElement.style.overflow = '';
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
      this.product = null;
    }

    handleKeydown(e) {
      if (!this.hasAttribute('open')) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        this.hide();
      } else if (e.key === 'Tab') {
        const focusables = this.content.querySelectorAll(this.focusableSelector);
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    trapFocus() {
      const focusables = this.content.querySelectorAll(this.focusableSelector);
      const first = focusables[0] || this.closeBtn;
      if (first) first.focus();
    }

    renderSkeleton() {
      return `
        <div class="kit-modal__skeleton" aria-label="Cargando producto">
          <div class="kit-modal__skeleton-img"></div>
          <div class="kit-modal__skeleton-line kit-modal__skeleton-line--lg"></div>
          <div class="kit-modal__skeleton-line"></div>
          <div class="kit-modal__skeleton-line kit-modal__skeleton-line--sm"></div>
        </div>`;
    }

    renderError() {
      return `
        <div class="kit-modal__error">
          <p>No pudimos cargar este producto. Intentá de nuevo.</p>
          <button type="button" class="kit-modal__error-close button button--primary">Cerrar</button>
        </div>`;
    }

    renderProduct() {
      const p = this.product;
      const v = this.selectedVariant;
      const count = KitBuilder.getCount();
      const target = KitBuilder.config.target;
      const remaining = Math.max(0, target - count);
      const images = (p.images && p.images.length
        ? p.images
        : (p.featured_image ? [p.featured_image] : [])
      ).map(src => src.replace(/_\d+x\d*(@2x)?\./, '_600x.'));

      const variantHtml = p.variants.length > 1 ? this.renderVariants() : '';
      const priceFormatted = KitBuilder.formatPrice(v.price);
      const descHtml = this.buildDescription(p);

      // Parse título para mostrar jerarquía: SKU + fragancia + marca
      const header = this.parseTitle(p.title);

      // Microcopy por estado del kit
      let remainingCopy, remainingState;
      if (count === 0) {
        remainingCopy = `Elegí <strong>${target}</strong> fragancias para armar tu kit`;
        remainingState = 'start';
      } else if (remaining > 0) {
        remainingCopy = `Te falta${remaining > 1 ? 'n' : ''} <strong>${remaining}</strong> para completar el kit de ${target}`;
        remainingState = 'progress';
      } else {
        remainingCopy = `Tu kit de ${target} ya está listo. Podés sumar más o ir al carrito.`;
        remainingState = 'complete';
      }

      const dotsHtml = images.length > 1 ? `
        <div class="kit-modal__dots" role="tablist" aria-label="Imágenes del producto">
          ${images.map((_, i) => `<button class="kit-modal__dot${i === 0 ? ' is-active' : ''}" type="button" role="tab" aria-label="Imagen ${i + 1} de ${images.length}" aria-selected="${i === 0}" data-slide="${i}"></button>`).join('')}
        </div>` : '';

      this.body.innerHTML = `
        <div class="kit-modal__product">
          <div class="kit-modal__media-wrap">
            <div class="kit-modal__media" data-image-count="${images.length}">
              ${images.map((src, i) => `<img src="${src}" alt="${this.escape(p.title)}" ${i === 0 ? 'loading="eager"' : 'loading="lazy"'} width="600" height="600">`).join('')}
            </div>
            ${dotsHtml}
          </div>
          <div class="kit-modal__info">
            <header class="kit-modal__header">
              ${header.sku ? `<span class="kit-modal__sku">${this.escape(header.sku)}</span>` : ''}
              <h2 class="kit-modal__title" id="kit-modal-title">${this.escape(header.name || p.title)}</h2>
              ${header.brand ? `<p class="kit-modal__brand">Inspirada en ${this.escape(header.brand)}</p>` : ''}
            </header>
            <div class="kit-modal__price">
              <div class="kit-modal__price-main">
                <span class="kit-modal__price-unit" data-price-display>${priceFormatted}</span>
                <span class="kit-modal__price-label">por unidad · ${KitBuilder.config.unitSize}</span>
              </div>
              <div class="kit-modal__price-combo">
                <svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" width="13" height="13"><path d="M2 4h12l-1 8H3L2 4z M6 4V2.5A1.5 1.5 0 0 1 7.5 1h1A1.5 1.5 0 0 1 10 2.5V4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                <span>${KitBuilder.config.target} x ${KitBuilder.formatPrice(KitBuilder.config.comboPrice)} · arma tu kit completo</span>
              </div>
            </div>
            ${descHtml}
            ${variantHtml}
            <div class="kit-modal__qty">
              <label class="kit-modal__qty-label" for="kit-modal-qty">Cantidad</label>
              <div class="kit-modal__qty-control">
                <button type="button" class="kit-modal__qty-btn" data-qty="minus" aria-label="Quitar una unidad">−</button>
                <input id="kit-modal-qty" class="kit-modal__qty-input" type="number" min="1" max="10" value="${this.quantity}" inputmode="numeric">
                <button type="button" class="kit-modal__qty-btn" data-qty="plus" aria-label="Sumar una unidad">+</button>
              </div>
            </div>
            <div class="kit-modal__remaining" data-state="${remainingState}" aria-live="polite">${remainingCopy}</div>
            <button type="button" class="kit-modal__cta button button--primary" data-kit-submit>
              <svg class="kit-modal__cta-icon" aria-hidden="true" focusable="false" viewBox="0 0 20 20" width="18" height="18">
                <path d="M3 5h2l2 9h9l2-7H6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                <circle cx="9" cy="17" r="1.2" fill="currentColor"/>
                <circle cx="15" cy="17" r="1.2" fill="currentColor"/>
              </svg>
              <span class="kit-modal__cta-label">Agregar al kit</span>
              <span class="kit-modal__cta-price">${priceFormatted}</span>
              <svg class="kit-modal__cta-spinner" aria-hidden="true" focusable="false" viewBox="0 0 66 66" width="20" height="20">
                <circle cx="33" cy="33" r="30" stroke="currentColor" stroke-width="6" fill="none"></circle>
              </svg>
            </button>
            <ul class="kit-modal__benefits" aria-label="Beneficios">
              <li>
                <svg aria-hidden="true" focusable="false" viewBox="0 0 20 20" width="14" height="14"><path d="M2 14V7h9v7M11 9h4l2 3v2h-6M4 14a2 2 0 1 0 4 0 2 2 0 0 0-4 0zM13 14a2 2 0 1 0 4 0 2 2 0 0 0-4 0z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
                Envío gratis en Colombia
              </li>
              <li>
                <svg aria-hidden="true" focusable="false" viewBox="0 0 20 20" width="14" height="14"><path d="M4 7l6-3 6 3v6l-6 3-6-3V7zM4 7l6 3 6-3M10 10v6" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
                Stock disponible
              </li>
            </ul>
          </div>
        </div>
      `;

      // Sincronizar dots del carousel con el scroll
      if (images.length > 1) this.bindCarouselDots();
    }

    /**
     * Extrae SKU, nombre de fragancia y marca del título del producto.
     * Titulos típicos:
     *   "Serené Le Parfum S032 - Inspirada en Silver Mountain Water por Creed (Kit 50 ml)"
     *   "Serené Le Parfum S032 - Inspirada en Silver Mountain Water por Creed - 30 ml"
     */
    parseTitle(title) {
      if (!title) return { sku: '', name: '', brand: '' };
      const skuMatch = title.match(/\bS\d{3,4}\b/);
      const sku = skuMatch ? skuMatch[0] : '';
      // Quitar "(Kit 50 ml)", " - 30 ml" y variantes del final
      let clean = title
        .replace(/\s*\(Kit\s*\d{2,3}\s*ml\)\s*$/i, '')
        .replace(/\s*[-·•]\s*\d{2,3}\s*ml\s*$/i, '')
        .trim();
      // Intentar separar "Inspirada en X por/By Y"
      const m = clean.match(/[Ii]nspirad[ao]\s+en:?\s*(.+?)\s+(?:por|[Bb]y)\s+(.+)$/);
      if (m) {
        return { sku, name: m[1].trim(), brand: m[2].trim() };
      }
      // Fallback: solo intenta "Inspirada en X"
      const m2 = clean.match(/[Ii]nspirad[ao]\s+en:?\s*(.+)$/);
      if (m2) {
        return { sku, name: m2[1].trim(), brand: '' };
      }
      return { sku, name: clean, brand: '' };
    }

    /**
     * Sincroniza los dots del carousel con el scroll horizontal del media.
     * Al tocar un dot, hace scroll al slide correspondiente.
     */
    bindCarouselDots() {
      const media = this.body.querySelector('.kit-modal__media');
      const dots = Array.from(this.body.querySelectorAll('.kit-modal__dot'));
      if (!media || dots.length === 0) return;
      // Click en dot → scroll al slide
      dots.forEach((dot, idx) => {
        dot.addEventListener('click', () => {
          const slideWidth = media.clientWidth;
          media.scrollTo({ left: slideWidth * idx, behavior: 'smooth' });
        });
      });
      // Scroll en el media → actualiza dot activo
      let raf = null;
      media.addEventListener('scroll', () => {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const idx = Math.round(media.scrollLeft / Math.max(1, media.clientWidth));
          dots.forEach((d, i) => {
            const active = i === idx;
            d.classList.toggle('is-active', active);
            d.setAttribute('aria-selected', active ? 'true' : 'false');
          });
        });
      }, { passive: true });
    }

    /**
     * Construye el HTML de la descripción del producto.
     * Usa el HTML seguro devuelto por /products/{handle}.js pero quita scripts
     * y limita la altura con CSS para no romper el modal.
     */
    buildDescription(p) {
      const raw = p.description || '';
      if (!raw.trim()) return '';
      // Sanitize: eliminar scripts y style tags
      const clean = raw
        .replace(/<script[\s\S]*?<\/script>/gi, '')
        .replace(/<style[\s\S]*?<\/style>/gi, '')
        .replace(/on\w+\s*=\s*(['"]).*?\1/gi, '');
      return `<details class="kit-modal__desc-toggle">
        <summary class="kit-modal__desc-summary">
          <span class="kit-modal__desc-summary-label">Descripción del producto</span>
          <svg class="kit-modal__desc-caret" aria-hidden="true" focusable="false" viewBox="0 0 16 16" width="14" height="14">
            <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </summary>
        <div class="kit-modal__description">${clean}</div>
      </details>`;
    }

    renderVariants() {
      const p = this.product;
      const options = p.options.map((name, idx) => {
        const values = [...new Set(p.variants.map((v) => v.options[idx]).filter(Boolean))];
        if (values.length <= 1) return '';
        return `
          <fieldset class="kit-modal__variant-group" data-option-index="${idx}">
            <legend class="kit-modal__variant-legend">${this.escape(name)}</legend>
            <div class="kit-modal__variant-options">
              ${values
                .map((val) => {
                  const checked = this.selectedVariant.options[idx] === val ? 'checked' : '';
                  return `
                    <label class="kit-modal__variant-option">
                      <input type="radio" name="option-${idx}" value="${this.escape(val)}" ${checked}>
                      <span>${this.escape(val)}</span>
                    </label>`;
                })
                .join('')}
            </div>
          </fieldset>`;
      });
      return options.join('');
    }

    // NOTA: el viejo bindProductEvents() se reemplazó por delegación de eventos
    // en el constructor (handleBodyClick, handleBodyChange, handleBodyInput).
    // De esta forma, cada vez que renderProduct() reescribe el innerHTML,
    // los listeners siguen funcionando sin necesidad de re-bindear.

    handleVariantChange() {
      const groups = this.body.querySelectorAll('.kit-modal__variant-group');
      const selectedOptions = [];
      groups.forEach((g) => {
        const checked = g.querySelector('input[type="radio"]:checked');
        if (checked) selectedOptions.push(checked.value);
      });

      const match = this.product.variants.find((v) => {
        return selectedOptions.every((opt, idx) => v.options[idx] === opt);
      });

      if (match) {
        this.selectedVariant = match;
        const priceEl = this.body.querySelector('[data-price-display]');
        const ctaLabel = this.body.querySelector('.kit-modal__cta-label');
        const priceFormatted = KitBuilder.formatPrice(match.price);
        if (priceEl) priceEl.textContent = priceFormatted;
        if (ctaLabel) ctaLabel.textContent = `Agregar al kit · ${priceFormatted}`;

        const submit = this.body.querySelector('[data-kit-submit]');
        if (submit) {
          submit.disabled = !match.available;
          if (!match.available) {
            const label = submit.querySelector('.kit-modal__cta-label');
            if (label) label.textContent = 'Agotado';
          }
        }
      }
    }

    async handleSubmit(e) {
      const btn = e.currentTarget;
      if (btn.disabled || btn.classList.contains('is-loading')) return;
      btn.classList.add('is-loading');
      btn.disabled = true;

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: this.selectedVariant.id, quantity: this.quantity }],
            sections: 'cart-icon-bubble,cart-drawer',
          }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || 'No se pudo agregar');
        }

        const data = await res.json();

        // 1. guardar en el kit
        KitBuilder.addItem({
          variantId: this.selectedVariant.id,
          productId: this.product.id,
          title: this.product.title,
          image: (this.product.featured_image || '').replace(/_\d+x\d*(@2x)?\./, '_200x.'),
          price: this.selectedVariant.price,
        });

        // 2. refrescar sections server-rendered (cart-icon-bubble, cart-drawer)
        if (data.sections) {
          this.updateSections(data.sections);
        }

        // 3. feedback visual en el botón
        const label = btn.querySelector('.kit-modal__cta-label');
        if (label) label.textContent = '✓ Agregado';
        btn.classList.remove('is-loading');
        btn.classList.add('is-success');

        // 4. auto cerrar y (si se completó) disparar celebración
        setTimeout(() => {
          this.hide();
          btn.classList.remove('is-success');
        }, 700);
      } catch (err) {
        btn.classList.remove('is-loading');
        btn.disabled = false;
        const label = btn.querySelector('.kit-modal__cta-label');
        if (label) label.textContent = 'Error, reintentá';
        setTimeout(() => {
          if (label) label.textContent = `Agregar al kit · ${KitBuilder.formatPrice(this.selectedVariant.price)}`;
        }, 1800);
      }
    }

    updateSections(sections) {
      Object.keys(sections).forEach((id) => {
        const html = sections[id];
        if (!html) return;
        try {
          const parsed = new DOMParser().parseFromString(html, 'text/html');

          // cart-icon-bubble
          if (id === 'cart-icon-bubble') {
            const target = document.getElementById('cart-icon-bubble');
            const src = parsed.querySelector('#shopify-section-cart-icon-bubble') || parsed.querySelector('.shopify-section');
            if (target && src) target.innerHTML = src.innerHTML;
          }

          // cart-drawer
          if (id === 'cart-drawer') {
            const target = document.querySelector('cart-drawer');
            const src = parsed.querySelector('cart-drawer');
            if (target && src) target.innerHTML = src.innerHTML;
          }
        } catch (e) {
          // fail safe
        }
      });
    }

    escape(s) {
      return String(s).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      }[c]));
    }
  }
  if (!customElements.get('kit-quick-add-modal')) {
    customElements.define('kit-quick-add-modal', KitQuickAddModal);
  }

  // ---------------------------------------------------------------
  // 4. <kit-progress-bar>  → barra sticky superior
  // ---------------------------------------------------------------
  class KitProgressBar extends HTMLElement {
    connectedCallback() {
      this.countEl = this.querySelector('[data-kit-count]');
      this.targetEl = this.querySelector('[data-kit-target]');
      this.fillEl = this.querySelector('[data-kit-fill]');
      this.statusEl = this.querySelector('[data-kit-status]');
      this.currentPriceEl = this.querySelector('[data-kit-current-price]');
      this.comboPriceEl = this.querySelector('[data-kit-combo-price]');
      this.resetBtn = this.querySelector('[data-kit-reset]');

      this.onStateChange = this.render.bind(this);
      this.onComplete = this.celebrate.bind(this);
      KitBuilder.events.addEventListener('kit:change', this.onStateChange);
      KitBuilder.events.addEventListener('kit:complete', this.onComplete);

      if (this.resetBtn) {
        this.resetBtn.addEventListener('click', () => {
          if (KitBuilder.getCount() === 0) return;
          const ok = window.confirm('¿Querés vaciar tu kit y empezar de nuevo?');
          if (ok) KitBuilder.clear();
        });
      }
      this.render();
    }

    disconnectedCallback() {
      KitBuilder.events.removeEventListener('kit:change', this.onStateChange);
      KitBuilder.events.removeEventListener('kit:complete', this.onComplete);
    }

    render() {
      const state = KitBuilder.getState();
      const count = state.items.length;
      const target = KitBuilder.config.target;
      const progress = Math.min(100, (count / target) * 100);

      if (this.countEl) this.countEl.textContent = count;
      if (this.targetEl) this.targetEl.textContent = target;
      if (this.fillEl) this.fillEl.style.width = progress + '%';

      this.classList.toggle('kit-progress--complete', count >= target);
      this.classList.toggle('kit-progress--empty', count === 0);

      if (this.statusEl) {
        if (count === 0) {
          this.statusEl.textContent = `Escogé tus ${target} fragancias favoritas`;
        } else if (count < target) {
          const falta = target - count;
          this.statusEl.textContent = `Te falta${falta > 1 ? 'n' : ''} ${falta} para completar tu kit`;
        } else {
          this.statusEl.textContent = `¡Kit completo! Revisá tu carrito`;
        }
      }

      if (this.resetBtn) {
        this.resetBtn.style.display = count > 0 ? 'inline-flex' : 'none';
      }

      if (this.currentPriceEl) {
        const current = count * (KitBuilder.config.regularPrice / target);
        this.currentPriceEl.textContent = KitBuilder.formatPrice(current);
      }
    }

    celebrate() {
      this.classList.add('kit-progress--celebrating');
      setTimeout(() => this.classList.remove('kit-progress--celebrating'), 1800);
    }
  }
  if (!customElements.get('kit-progress-bar')) {
    customElements.define('kit-progress-bar', KitProgressBar);
  }

  // ---------------------------------------------------------------
  // 5. <kit-sticky-cta>  → CTA fijo inferior mobile
  // ---------------------------------------------------------------
  class KitStickyCta extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button, a');
      this.countEl = this.querySelector('[data-kit-count]');
      this.targetEl = this.querySelector('[data-kit-target]');
      this.labelEl = this.querySelector('[data-kit-cta-label]');
      this.cartUrl = this.dataset.cartUrl || '/cart';

      this.onStateChange = this.render.bind(this);
      KitBuilder.events.addEventListener('kit:change', this.onStateChange);

      if (this.button) this.button.addEventListener('click', this.handleClick.bind(this));
      this.render();
    }

    disconnectedCallback() {
      KitBuilder.events.removeEventListener('kit:change', this.onStateChange);
    }

    handleClick(e) {
      const count = KitBuilder.getCount();
      const target = KitBuilder.config.target;
      // 2026-05-25: Siempre intentar abrir el cart-drawer lateral, sin redirect a /cart.
      // Aplica tanto para count < target (ver progreso) como para count >= target (kit listo).
      e.preventDefault();
      if (!openCartDrawerSafely()) {
        // Fallback duro únicamente si el theme no tiene cart-drawer disponible
        window.location.href = this.cartUrl;
      }
    }

    render() {
      const count = KitBuilder.getCount();
      const target = KitBuilder.config.target;

      this.classList.toggle('kit-sticky--visible', count > 0);
      this.classList.toggle('kit-sticky--complete', count >= target);

      if (this.countEl) this.countEl.textContent = count;
      if (this.targetEl) this.targetEl.textContent = target;
      if (this.labelEl) {
        this.labelEl.textContent = count >= target
          ? `¡Kit completo! Ir al carrito`
          : `Ver mi kit (${count}/${target})`;
      }
    }
  }
  if (!customElements.get('kit-sticky-cta')) {
    customElements.define('kit-sticky-cta', KitStickyCta);
  }

  // ---------------------------------------------------------------
  // 6. Click delegation: imagen/área de la card → silent add (2026-05-25)
  //    Antes: abría kit-quick-add-modal. Ahora: silent add directo.
  //    Si el trigger es un anchor/image dentro de la card, el opener
  //    real para el feedback visual debe ser el botón <kit-add-button>
  //    asociado (mismo card). Si no encuentra el botón, fallback al
  //    trigger original (sin feedback visible).
  // ---------------------------------------------------------------
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-kit-open-modal]');
    if (!trigger) return;
    e.preventDefault();
    // Buscar botón asociado en la misma card para feedback visual coherente
    const card = trigger.closest('[data-kit-card]') || trigger.parentElement;
    const btn = card ? card.querySelector('kit-add-button button') : null;
    silentAddProduct({
      productUrl: trigger.dataset.productUrl,
      productId: trigger.dataset.productId,
      opener: btn || trigger,
    });
  });

  // ---------------------------------------------------------------
  // 7. Card state sync: marca cards en el kit con clase + badge
  // ---------------------------------------------------------------
  const syncCards = () => {
    document.querySelectorAll('[data-kit-card]').forEach((card) => {
      const productId = card.dataset.productId;
      const inKit = KitBuilder.hasProduct(productId);
      card.classList.toggle('kit-card--in-kit', inKit);
      const badge = card.querySelector('[data-kit-card-badge]');
      if (badge) {
        badge.style.display = inKit ? 'inline-flex' : 'none';
      }
    });
  };
  KitBuilder.events.addEventListener('kit:change', syncCards);

  // ---------------------------------------------------------------
  // 8. Celebration: al completar el kit 30ml (rev 2026-05-25).
  //
  //    Comportamiento nuevo (user pidió cart-drawer lateral, NO popup
  //    ni redirect a /cart):
  //      1. ensureSurpriseInCart — auto-add Fragancia Sorpresa $0 si falta
  //      2. waitForCartStable — espera reconcile de cart-rewards / Shrine Pro
  //      3. refreshCartDrawerSection — refresca HTML server-rendered del
  //         cart-drawer via Section Rendering API ANTES de abrirlo, para
  //         evitar el "drawer en blanco" / HTML stale documentado antes.
  //      4. drawer.open() — abre cart-drawer lateral.
  //      5. Fallback duro a /cart si drawer.open no existe / falla.
  //
  //    Mitigaciones a los race conditions documentados originalmente:
  //      - cart-rewards.js gift add → waitForCartStable (mantiene)
  //      - Shrine Pro re-render con HTML stale → refresh server-rendered
  //        ANTES de abrir (no innerHTML stale del cache local)
  //      - EasySell timing → no se toca, sigue como hoy
  //
  //    Aislado al kit 30ml. kit-builder.js (50ml) intacto.
  // ---------------------------------------------------------------
  async function waitForCartStable(maxWaitMs) {
    const start = Date.now();
    let lastCount = -1;
    let stableSince = 0;
    let lastCart = null;
    while (Date.now() - start < maxWaitMs) {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (res.ok) {
          const cart = await res.json();
          lastCart = cart;
          if (cart.item_count === lastCount) {
            if (stableSince === 0) stableSince = Date.now();
            if (Date.now() - stableSince >= 400) return cart;
          } else {
            stableSince = 0;
            lastCount = cart.item_count;
          }
        }
      } catch (e) { /* retry */ }
      await new Promise((r) => setTimeout(r, 180));
    }
    return lastCart;
  }

  /**
   * Refresca el HTML server-rendered del cart-drawer y cart-icon-bubble
   * usando Section Rendering API. Se llama ANTES de abrir el drawer al
   * completar el kit, para evitar "drawer en blanco" / HTML stale.
   * Preserva 'active' (patrón validado en kit-50ml-b upsell).
   */
  async function refreshCartDrawerSection() {
    try {
      const r = await fetch('/?sections=cart-drawer,cart-icon-bubble', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!r.ok) return;
      const sections = await r.json();
      updateCartSections(sections);
    } catch (e) { /* noop — fallback drawer.open() opera con HTML actual */ }
  }

  /**
   * Abre el cart-drawer lateral del theme (Shrine Pro / Dawn-family).
   * Retorna true si lo abrió, false si no encontró el elemento o no pudo.
   */
  function openCartDrawerSafely() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return false;
    try {
      if (typeof drawer.open === 'function') {
        drawer.open();
        return true;
      }
      // Fallback: trigger click en el cart-icon-bubble link
      const bubble = document.getElementById('cart-icon-bubble');
      const link = bubble ? bubble.querySelector('a') : null;
      if (link) {
        link.click();
        return true;
      }
    } catch (e) { /* fall through */ }
    return false;
  }

  // ---------------------------------------------------------------
  // Auto-add Fragancia Sorpresa Serené (Col 30 ml) cuando el kit completa.
  // Variant ID dedicada $0 — sólo se añade si NO está ya en el carrito.
  // Aislado al kit 30ml (kit-builder.js de 50ml NO tiene esta lógica).
  // ---------------------------------------------------------------
  const SURPRISE_VARIANT_ID = 45309590044706;  // fragancia-sorpresa-serene-col-30-ml ($0, tracked:false)

  async function ensureSurpriseInCart() {
    try {
      const r = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!r.ok) return false;
      const cart = await r.json();
      const already = (cart.items || []).some(function (it) {
        return Number(it.variant_id) === SURPRISE_VARIANT_ID;
      });
      if (already) return true;  // idempotente — no duplicar

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: SURPRISE_VARIANT_ID, quantity: 1 }),
      });
      return addRes.ok;
    } catch (e) {
      return false;
    }
  }

  let __kitCompleteHandled = false;
  KitBuilder.events.addEventListener('kit:complete', async () => {
    if (__kitCompleteHandled) return;
    __kitCompleteHandled = true;
    try {
      // 1. Auto-add Fragancia Sorpresa $0 (idempotente)
      await ensureSurpriseInCart();
      // 2. Esperar estabilidad del cart (cart-rewards reconcile, EasySell, etc.)
      await waitForCartStable(2500);
      // 3. Refrescar cart-drawer y cart-icon-bubble con HTML server-rendered actual
      //    ANTES de abrir, para evitar el "drawer en blanco" / HTML stale.
      await refreshCartDrawerSection();
      // 4. Abrir el cart-drawer lateral.
      const opened = openCartDrawerSafely();
      // 5. Fallback duro a /cart sólo si no se pudo abrir el drawer.
      if (!opened) {
        window.location.href = '/cart';
      }
    } catch (e) {
      // Fallback duro ante cualquier error inesperado
      window.location.href = '/cart';
    }
  });

  // ---------------------------------------------------------------
  // 9. Init: sync con cart real al cargar + render inicial
  // ---------------------------------------------------------------
  const init = () => {
    KitBuilder.syncWithCart();
    syncCards();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
